import React, { useState } from 'react';
import {
  FiServer,
  FiDatabase,
  FiZap,
  FiHardDrive,
  FiBox,
  FiLayers,
  FiCpu,
} from 'react-icons/fi';
import {
  formatCurrency,
  formatDate,
  formatRecommendationTitle,
  getStatusColor,
  getConfidenceColor,
  getImpactColor,
  getEffortColor,
  getImpactLevel,
  getEffortLevel,
  calculateAnnualSavings,
} from '../utils/costOptimizationHelpers';
import ProjectAssignmentSelect from '../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../components/WorkloadAssignmentSelect';
import { assignRecommendationWorkload } from '../../../services/cloudopsApi';

interface RecommendationMetaData {
  region?: string;
  instance_type?: string;
  [key: string]: unknown;
}

interface RecommendationData {
  id: string;
  service: string;
  type: string;
  status: string;
  monthly_savings: number;
  confidence: string;
  reasoning: string;
  meta_data: RecommendationMetaData | null;
  detected_at: string;
  resource_id: string;
  project_id?: string | null;
  recommended_action?: string | null;
  risk_level?: string | null;
  risk_factors?: string[] | null;
  action_steps?: string[] | null;
  workload_type?: string | null;
  workload_id?: string | null;
  workload_name?: string;
}

interface RecommendationCardProps {
  recommendation: RecommendationData;
  onStatusUpdate?: (id: string, status: string) => Promise<void>;
  onProjectAssign?: (id: string, projectId: string | null) => Promise<void>;
  updating: boolean;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({ recommendation, onStatusUpdate, onProjectAssign, updating }) => {
  const [showDetails, setShowDetails] = useState(false);

  const {
    id,
    service,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    type: _type,
    status,
    monthly_savings,
    confidence,
    reasoning,
    meta_data,
    detected_at,
    resource_id,
    project_id,
  } = recommendation;

  const title = formatRecommendationTitle(recommendation as { service: string; type: 'Idle' | 'Rightsizing' | 'Reserved Instances' | 'Storage Optimization' });
  const annualSavings = calculateAnnualSavings(monthly_savings);
  const impact = getImpactLevel(monthly_savings);
  const effort = getEffortLevel(service);

  // Get service icon
  const getServiceIcon = () => {
    const iconProps = { className: 'w-5 h-5' };
    switch (service) {
      case 'EC2':
        return <FiServer {...iconProps} />;
      case 'RDS':
      case 'DynamoDB':
        return <FiDatabase {...iconProps} />;
      case 'Lambda':
        return <FiZap {...iconProps} />;
      case 'S3':
        return <FiHardDrive {...iconProps} />;
      case 'ECS':
      case 'EKS':
        return <FiBox {...iconProps} />;
      case 'ALB':
        return <FiLayers {...iconProps} />;
      case 'SageMaker':
      case 'Bedrock':
        return <FiCpu {...iconProps} />;
      case 'EBS':
        return <FiHardDrive {...iconProps} />;
      case 'Glue':
        return <FiDatabase {...iconProps} />;
      case 'EMR':
        return <FiServer {...iconProps} />;
      default:
        return <FiServer {...iconProps} />;
    }
  };

  const handleDismiss = async () => {
    if (onStatusUpdate) {
      await onStatusUpdate(id, 'Dismissed');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`px-2 py-1 text-xs font-semibold rounded ${getStatusColor(status)}`}>
              {status}
            </span>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            {onProjectAssign && (
              <ProjectAssignmentSelect
                currentProjectId={project_id}
                onAssign={(projectId) => onProjectAssign(id, projectId)}
                disabled={updating}
              />
            )}
            <WorkloadAssignmentSelect
              currentWorkloadId={recommendation.workload_id}
              onAssign={async (workloadId) => {
                await assignRecommendationWorkload(recommendation.id, workloadId);
              }}
              disabled={updating}
            />
            {recommendation.workload_name && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                {recommendation.workload_name}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {reasoning}
          </p>
        </div>
      </div>

      {/* Resource Details */}
      <div className="flex items-center gap-2 mb-4 text-sm text-gray-600 dark:text-gray-400">
        {getServiceIcon()}
        <span className="font-medium">{service}</span>
        <span className="text-gray-400 dark:text-gray-500">•</span>
        <span>{resource_id}</span>
        {meta_data?.region && (
          <>
            <span className="text-gray-400 dark:text-gray-500">•</span>
            <span>{meta_data.region}</span>
          </>
        )}
        {meta_data?.instance_type && (
          <>
            <span className="text-gray-400 dark:text-gray-500">•</span>
            <span className="font-mono">{meta_data.instance_type}</span>
          </>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Monthly Savings</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCurrency(monthly_savings)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Annual Savings</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCurrency(annualSavings)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Confidence</p>
          <p className={`text-sm font-medium ${getConfidenceColor(confidence)}`}>
            {confidence}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Effort</p>
          <p className={`text-sm font-medium ${getEffortColor(effort)}`}>
            {effort}
          </p>
        </div>
      </div>

      {/* Impact + AI Fields */}
      <div className="flex flex-wrap gap-6 mb-4">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Impact</p>
          <p className={`text-sm font-medium ${getImpactColor(impact)}`}>
            {impact}
          </p>
        </div>
        {recommendation.recommended_action && (
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Action</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
              {recommendation.recommended_action}
            </p>
          </div>
        )}
        {recommendation.risk_level && (
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Risk</p>
            <p className={`text-sm font-medium capitalize ${
              recommendation.risk_level === 'high' ? 'text-red-600 dark:text-red-400' :
              recommendation.risk_level === 'medium' ? 'text-yellow-600 dark:text-yellow-400' :
              'text-green-600 dark:text-green-400'
            }`}>
              {recommendation.risk_level}
            </p>
          </div>
        )}
        {recommendation.workload_type && (
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Workload</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
              {recommendation.workload_type.replace(/_/g, ' ')}
            </p>
          </div>
        )}
      </div>

      {/* Action Steps */}
      {recommendation.action_steps && recommendation.action_steps.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Action Steps</p>
          <ol className="list-decimal list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
            {recommendation.action_steps.map((step, idx) => (
              <li key={idx}>{step}</li>
            ))}
          </ol>
        </div>
      )}

      {/* Risk Factors */}
      {recommendation.risk_factors && recommendation.risk_factors.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Risk Factors</p>
          <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
            {recommendation.risk_factors.map((factor, idx) => (
              <li key={idx}>{factor}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Created: {formatDate(detected_at)}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleDismiss}
            disabled={updating || status !== 'Open'}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Dismiss
          </button>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            View Details
          </button>
        </div>
      </div>

      {/* Details Modal/Drawer */}
      {showDetails && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Resource Details</h4>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-4">
            <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
              {JSON.stringify(meta_data, null, 2)}
            </pre>
          </div>
          <button
            onClick={() => setShowDetails(false)}
            className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Close Details
          </button>
        </div>
      )}
    </div>
  );
};

export default RecommendationCard;
